/**
 * Reusable frog mascot image component
 *
 * Maps variant names to image paths in `/images/frog/` and handles
 * sizing via the `size` prop. All images have transparent backgrounds.
 */

/** Available frog mascot variants */
export type FrogMascotVariant =
  | 'default'
  | 'tongue'
  | 'chewing'
  | 'done'
  | 'rolling'
  | 'thinking'
  | 'settings'
  | 'lost'
  | 'test'
  | 'unlucky'
  | 'lucky'
  | 'error'
  | 'waiting'
  | 'notfound'
  | 'disconnected'
  | 'loading'
  | 'welcome'
  | 'logo';

/** Available size presets */
export type FrogMascotSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface FrogMascotProps {
  /** Which frog image variant to display */
  variant?: FrogMascotVariant;
  /** Size preset: sm (40px), md (80px), lg (120px), xl (180px), 2xl (360px) */
  size?: FrogMascotSize;
  /** Optional additional CSS class names */
  className?: string;
}

/** Map each variant to its image filename */
export const VARIANT_FILES: Record<FrogMascotVariant, string> = {
  default: 'dice-frog.png',
  tongue: 'frog-tongue.png',
  chewing: 'frog-chewing.png',
  done: 'frog-done.png',
  rolling: 'frog-rolling.png',
  thinking: 'frog-thinking.png',
  settings: 'settings-frog.png',
  lost: 'lost-frog.png',
  test: 'test-frog.png',
  unlucky: 'unlucky-frog.png',
  lucky: 'lucky-frog.png',
  error: 'error-frog.png',
  waiting: 'waiting-frog.png',
  notfound: '404-frog.png',
  disconnected: 'disconnected-frog.png',
  loading: 'loading-frog.png',
  welcome: 'welcome-frog.png',
  logo: 'logo-dice-frog.png'
};

/** Map each variant to descriptive alt text */
export const VARIANT_ALT: Record<FrogMascotVariant, string> = {
  default: 'Dice Frog',
  tongue: 'Frog catching dice',
  chewing: 'Frog munching dice',
  done: 'Frog with result',
  rolling: 'Frog watching dice roll',
  thinking: 'Frog thinking',
  settings: 'Frog mechanic',
  lost: 'Frog reading map',
  test: 'Frog taking exam',
  unlucky: 'Sad frog with nat 1',
  lucky: 'Proud frog with nat 20',
  error: 'Frog with error papers',
  waiting: 'Frog by campfire',
  notfound: 'Frog at crossroads',
  disconnected: 'Frog with broken rope',
  loading: 'Frog with hourglass',
  welcome: 'Frog holding welcome flag',
  logo: 'Dice Frog logo'
};

/** Map size presets to pixel values */
const SIZE_PX: Record<FrogMascotSize, number> = {
  sm: 40,
  md: 80,
  lg: 120,
  xl: 180,
  '2xl': 360
};

/**
 * Displays a frog mascot image with consistent sizing and alt text.
 *
 * @example
 * <FrogMascot variant="tongue" size="lg" />
 * <FrogMascot variant="logo" size="xl" className="mb-4" />
 */
export function FrogMascot({ variant = 'default', size = 'md', className }: FrogMascotProps) {
  const px = SIZE_PX[size];

  return (
    <img
      src={`/images/frog/${VARIANT_FILES[variant]}`}
      alt={VARIANT_ALT[variant]}
      width={px}
      height={px}
      className={className}
    />
  );
}
